import { MILLISECONDS, ITask, IUpdateTaskAssignedTo } from 'types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { loggedInUser } from 'hooks/use-logged-in-user';
import { useTaskService } from 'hooks/services/use-task-service';
import { useMemo } from 'react';
import { calculateSLAStatus } from 'packages/sla';
import { useLocation } from 'react-router';

interface TeamTasksCache {
  isTasksLoading: boolean;
  isTasksRefreshing: boolean;
  teamTasks: ITask[];
  hasTasks: boolean;
  teamTasksCount: number;
  myTasksCount: number;
  handleUpdateAssignedTo: (assignedTo: IUpdateTaskAssignedTo) => Promise<void>;
  refreshTeamTasks: () => void;
  reloadTeamTasks: () => void;
}

export const useTeamTasksCache = (teamId?: string): TeamTasksCache => {
  const location = useLocation();
  const taskService = useTaskService();
  const queryClient = useQueryClient();
  const { userId, hasTeams, myTeams } = loggedInUser.useController();

  const locationMemberId = location?.state?.paramsData?.memberId;

  const teamTaskIds = useMemo(
    () => (!hasTeams ? [] : !Boolean(teamId) || teamId === 'ALL' ? myTeams.map((t) => t.id) : [teamId!]),
    [teamId, myTeams, hasTeams]
  );

  const teamTasksQuery = useQuery({
    queryKey: ['teamTasks', teamTaskIds],
    queryFn: () => taskService.getTeamTasks(teamTaskIds),
    enabled: Boolean(teamTaskIds.length),
    refetchInterval: !Boolean(locationMemberId) ? MILLISECONDS.SECOND * 30 : false
  });

  // Refresh tasks invalidates the query and acts as a refetch
  const refreshTeamTasks = () => {
    queryClient.invalidateQueries({ queryKey: ['teamTasks'] });
  };

  // Reload tasks resets the query and acts like an initial fetch
  const reloadTeamTasks = () => {
    queryClient.resetQueries({ queryKey: ['teamTasks'] });
  };

  const handleUpdateAssignedTo = (assignedTo: IUpdateTaskAssignedTo) => {
    return taskService.updateAssignedTo(assignedTo).then((taskResponse) => {
      queryClient.setQueryData(['teamTasks', teamTaskIds], (oldData: ITask[]) => {
        let updatedData = [...oldData];
        const taskIndex = oldData.map((m) => m.taskId).indexOf(taskResponse.taskId);
        if (taskIndex !== -1) {
          updatedData.splice(taskIndex, 1);
        }
        if (teamTaskIds.includes(taskResponse.assignedToGroupId!)) {
          updatedData.unshift(taskResponse);
        }
        return updatedData;
      });
    });
  };

  const teamTasks = teamTasksQuery.data
    ? teamTasksQuery.data.map((task) => {
        const taskSLAStatus = calculateSLAStatus(task);
        return {
          ...task,
          slaStatus: taskSLAStatus
        };
      })
    : [];
  const isTasksLoading = teamTasksQuery.isPending || teamTasksQuery.isLoading;
  const isTasksRefreshing = teamTasksQuery.isRefetching;
  const hasTasks = Boolean(teamTasks.length);
  const myTasksCount = teamTasks.filter((t) => t.assignedToUserId === userId).length;
  const teamTasksCount = teamTasks.length;

  return {
    isTasksLoading,
    isTasksRefreshing,
    teamTasks,
    hasTasks,
    myTasksCount,
    teamTasksCount,
    handleUpdateAssignedTo,
    refreshTeamTasks,
    reloadTeamTasks
  };
};

export default useTeamTasksCache;
